import { React, classNames } from '@digsup/core-ui';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import { Button } from '@digsup/components-button';
import { Icon } from '@digsup/components-icon';
import { styles } from './Modal.scss';

const cx = classNames.bind(styles);

export const Modal = props => {
  const {
    modalIsOpen,
    onRequestClose,
    theme,
    withOverlayBackground,
    withCloseBtn,
    children,
    overlayClassName,
    className,
    size,
    verticalAlign,
    shouldCloseOnOverlayClick,
    shouldCloseOnEsc,
    shadow,
    spacing,
    coverMobile,
    ...restProps
  } = props;
  const classes = cx(
    'c-modal',
    {
      [`t-${theme}`]: !!theme,
      [`c-modal--size-${size}`]: !!size,
      [`c-modal--shadow-${shadow}`]: !!shadow,
      [`c-modal--spacing-${spacing}`]: !!spacing,
      'c-modal--cover-mobile': coverMobile,
      [`c-modal--valign-${verticalAlign}`]: !!verticalAlign,
    },
    className,
  );
  const overlayClasses = cx(
    'c-modal-overlay',
    {
      'c-modal-overlay--with-background': !!withOverlayBackground,
      [`t-${theme}`]: !!theme,
    },
    overlayClassName,
  );
  const wrapperClasses = cx('c-modal-wrapper', {
    [`c-modal-wrapper--size-${size}`]: !!size,
    'c-modal-wrapper--cover-mobile': coverMobile,
  });

  return (
    <ReactModal
      htmlOpenClassName="html--modal-open"
      bodyOpenClassName="body--modal-open"
      ariaHideApp={false}
      closeTimeoutMS={300}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      isOpen={modalIsOpen}
      className={wrapperClasses}
      overlayClassName={overlayClasses}
      onRequestClose={onRequestClose}
      shouldCloseOnEsc={shouldCloseOnEsc}
      {...restProps}>
      <div className="c-modal-click-capture" onClick={onRequestClose} />
      <div className={classes}>
        <div className="c-modal__body">{children}</div>
        {withCloseBtn && (
          <Button
            size="small"
            onClick={onRequestClose}
            iconOnly
            variant="flat"
            icon={<Icon name="x" size="small" />}
            className="c-modal__close-btn">
            Close
          </Button>
        )}
      </div>
    </ReactModal>
  );
};

Modal.propTypes = {
  /**
   * Boolean describing if the modal should be shown or not.
   */
  modalIsOpen: PropTypes.bool,
  /**
   * Boolean describing if the modal should be closed on overlay click.
   */
  shouldCloseOnOverlayClick: PropTypes.bool,
  /**
   * Boolean describing if the modal should be closed on Esc.
   */
  shouldCloseOnEsc: PropTypes.bool,
  /**
   * Boolean describing if the modal overlay has background.
   */
  withOverlayBackground: PropTypes.bool,
  /**
   * Boolean describing if the modal has close button.
   */
  withCloseBtn: PropTypes.bool,
  /**
   * Function that will be run when click on close icon/ escape button / overlay click.
   */
  onRequestClose: PropTypes.func,
  /**
   * Modal theme.
   */
  theme: PropTypes.oneOf(['none', 'danger', 'important', 'reward', 'positive']),
  /**
   * Modal vertical align.
   */
  verticalAlign: PropTypes.oneOf(['top', 'center', 'bottom']),
  /**
   * Modal size.
   */
  size: PropTypes.oneOf(['small', 'medium', 'large', 'auto']),
  /**
   * Modal padding.
   */
  spacing: PropTypes.oneOf(['none', 'medium', 'large']),
  /**
   * Modal shadow.
   */
  shadow: PropTypes.oneOf(['heavy', 'light']),
  /**
   * Modal additional className.
   */
  className: PropTypes.string,
  /**
   * Modal overlay additional className.
   */
  overlayClassName: PropTypes.string,
  /**
   * Cover viewport on mobile
   */
  coverMobile: PropTypes.bool,

  children: PropTypes.any,
};

Modal.defaultProps = {
  withOverlayBackground: true,
  shouldCloseOnOverlayClick: true,
  withCloseBtn: true,
  verticalAlign: 'center',
  size: 'small',
  spacing: 'medium',
  shadow: 'light',
  coverMobile: false,
  className: '',
  overlayClassName: '',
  modalIsOpen: false,
};
